/**
 * Innovation + Investor Center — sticky intro copy on the left, a stack of
 * investor-center link rows on the right (stock price, reports, calendar).
 * Ported from the merged `.im` block.
 */

import AgntTextIcon from 'components/agnt-inc/agnt-text-icon';

import styles from './innovation-investor.module.scss';

import type { InnovationInvestor as InnovationInvestorContent, InvestorRow } from '../content';

function RowArrow() {
  return (
    <span className={styles.rowArrow} aria-hidden="true">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
        <path d="M5 12h14M13 6l6 6-6 6" />
      </svg>
    </span>
  );
}

function Row({ row }: { row: InvestorRow }) {
  return (
    <a className={styles.row} href={row.href} target="_blank" rel="noopener noreferrer">
      <div className={styles.rowBody}>
        <div className={styles.rowTag}>{row.tag}</div>
        {row.figure ? <div className={styles.rowFigure}>{row.figure}</div> : null}
        {row.title ? <div className={styles.rowTitle}>{row.title}</div> : null}
        <div className={styles.rowSub}>{row.subtitle}</div>
      </div>
      <RowArrow />
    </a>
  );
}

// Fills the live-quote row with the SSR-provided price and appends the delay
// note to its subtitle. Other rows pass through untouched; when no price is
// available the row keeps its static fallback ("Live quote coming soon").
function withStockPrice(row: InvestorRow, stockPrice?: string | null): InvestorRow {
  if (row.kind !== 'stock' || !stockPrice) return row;
  return { ...row, figure: stockPrice, title: undefined, subtitle: `${row.subtitle} · Delayed 20 min` };
}

export default function InnovationInvestor({
  content,
  stockPrice,
}: {
  content: InnovationInvestorContent;
  stockPrice?: string | null;
}) {
  return (
    <div className={styles.section}>
      <div className={styles.container}>
        <div className={styles.grid}>
          <div className={styles.left} data-reveal>
            <div className={styles.eyebrow}>{content.eyebrow}</div>
            <h2 className={styles.heading}>{content.heading}</h2>
            <p className={styles.body}>{content.body}</p>
            <a className={styles.learnMore} href={content.cta.href} target="_blank" rel="noopener noreferrer">
              {content.cta.label}
            </a>
            <div className={styles.divider} />
            <div className={styles.rowsHead}>
              <AgntTextIcon href={content.rowsLink.href} label={content.rowsLink.label} />
            </div>
          </div>
          <div className={styles.right} data-reveal>
            {content.rows.map(row => (
              <Row key={row.tag} row={withStockPrice(row, stockPrice)} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
